import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Menu, Tooltip, Typography } from 'antd';
import type { NavigateFunction } from 'react-router-dom';
import {
  EllipsisRowTooltip,
  FilteredBuilding,
  FilteredFloor,
  FilteredSite,
  MENU_STYLE,
  MenuItem,
  RoomZoneItem,
  SIDEBAR_TREE_THEME,
  collectAllKeys,
  findAncestorChain,
  isInAllFloorsView,
  markSelected,
  maybeChildren,
  useTreeOpenKeys,
} from './shared';
import { Icon } from '../../primitives/icon/Icon';
import { MiddleTruncatedText } from '@/components/primitives/MiddleTruncatedText';
import { BuildingEditMenu } from '../BuildingEditMenu';
import { SiteEditMenu } from '../SiteEditMenu';
import { CreateDropdownButton } from '../CreateDropdownButton';
import { updateUrlParams } from '../utils';

interface StudioTreeProps {
  sites: FilteredSite[];
  filterValue: string;
  search: string;
  navigate: NavigateFunction;
}

/**
 * Studio's All Spaces tree.
 *
 * Same single-Menu structure as Monitor — Site and Building are SubMenus,
 * Floor is a SubMenu, Room/Zone are leaves. Studio's canvas keys off
 * `?spaceId=<floor>` so neither Site nor Building is selectable: clicking
 * their headers only toggles SubMenu expand/collapse. Building hover/active
 * pill is suppressed via the `sidebar-studio-menu` CSS scope so the rows
 * read as section headers, not selectable items.
 *
 * On the module-transition into Studio, any URL with `?buildingId=…` (and
 * no `?spaceId=…`) is rewritten to the first accessible floor under that
 * Building so the canvas has something to render.
 */
export const StudioTree: React.FC<StudioTreeProps> = ({ sites, filterValue, search, navigate }) => {
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      scrollTopRef.current = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  // Restores the user's scroll position after antd Menu re-renders that
  // would otherwise reset scrollTop to 0. No dep array because we want
  // to re-assert scrollTop after any render — the equality guard skips
  // the redundant DOM write when the value already matches.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollTopRef.current === 0) return;
    if (el.scrollTop === scrollTopRef.current) return;
    el.scrollTop = scrollTopRef.current;
  });

  // Studio is a floor-level surface: only the Floor is "selected" in the
  // sidebar, even when the URL has a `roomId` / `zoneId`. Room and zone
  // rows act as filters / quick-jump shortcuts — clicking them still
  // updates the URL (so the canvas can focus on that room/zone), but the
  // sidebar visually keeps the Floor as the selected row.
  const selectedKey = useMemo<string | null>(() => {
    const params = new URLSearchParams(search);
    return params.get('spaceId') ?? null;
  }, [search]);

  const items = useMemo<MenuItem[]>(() => {
    return sites.map((site) => buildSiteItem(site, { filterValue, navigate }));
  }, [sites, filterValue, navigate]);

  // The buildingId → first-floor redirect lives in DefaultSpaceRedirector
  // (mounted unconditionally in Sidebar.tsx) so it fires even when the
  // sidebar starts collapsed and StudioTree never mounts.

  // Auto-expand to selection.
  useEffect(() => {
    if (!selectedKey) return;
    const chain = findAncestorChain(items, selectedKey);
    if (!chain || chain.length === 0) return;
    const ancestorsOnly = chain.slice(0, -1);
    if (ancestorsOnly.length === 0) return;
    setOpenKeys((prev) => {
      const merged = Array.from(new Set([...prev, ...ancestorsOnly]));
      if (merged.length === prev.length) return prev;
      return merged;
    });
  }, [selectedKey, items]);

  const selectedKeys = useMemo(() => (selectedKey ? [selectedKey] : []), [selectedKey]);

  const handleShowAllFloors = useCallback(() => {
    const next: string[] = [];
    items.forEach((site) => {
      next.push(site.key);
      site.children?.forEach((building) => next.push(building.key));
    });
    setOpenKeys(next);
  }, [items]);

  // Centralized derivations: scaffolding keys + collapse-all + search-aware
  // auto-expand (which respects user-driven closes during the search).
  const { openKeysWithSearch, handleCollapseAllRooms, collapseAllRoomsVisible, recordUserClose } =
    useTreeOpenKeys({ items, filterValue, openKeys, setOpenKeys });

  // Studio force-injects Site AND Building keys into the Menu's `openKeys`
  // prop below (both are non-clickable section headers). Reading the raw
  // state directly would say "site/building not open" at mount and the
  // "Show all floors" button would be visible despite the tree already
  // being in the canonical all-floors layout.
  const effectiveOpenKeys = useMemo(() => {
    const forced: string[] = [];
    items.forEach((site) => {
      forced.push(String(site.key));
      (site.children ?? []).forEach((b) => forced.push(String(b.key)));
    });
    return Array.from(new Set([...forced, ...openKeysWithSearch]));
  }, [items, openKeysWithSearch]);
  const showAllFloorsVisible = useMemo(
    () => !isInAllFloorsView(items, effectiveOpenKeys),
    [items, effectiveOpenKeys],
  );

  // Hoist `markSelected` out of the per-site `items.map` so we don't
  // recursively clone every node + hand antd Menu a fresh `items`
  // reference on every render.
  const markedItems = useMemo(
    () => markSelected(items, selectedKey) ?? items,
    [items, selectedKey],
  );

  return (
    <>
      <div className="flex items-center justify-between my-0.5" style={{ padding: '6px 12px' }}>
        <Typography.Text strong type="secondary" className="text-xs">
          All Spaces
        </Typography.Text>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {collapseAllRoomsVisible && (
            <Tooltip title="Collapse all rooms">
              <Button
                type="text"
                size="small"
                icon={<Icon name="icon-layercollapseoutlined" size={16} />}
                onClick={handleCollapseAllRooms}
                aria-label="Collapse all rooms"
                data-id="collapse-all-rooms"
              />
            </Tooltip>
          )}
          {showAllFloorsVisible && (
            <Tooltip title="Show all floors">
              <Button
                type="text"
                size="small"
                icon={<Icon name="flooronlyoutlined" size={16} />}
                onClick={handleShowAllFloors}
                aria-label="Show all floors"
                data-id="show-all-floors"
              />
            </Tooltip>
          )}
          <CreateDropdownButton variant="icon" />
        </span>
      </div>
      <div
        ref={scrollRef}
        className="overflow-auto sidebar-scroll"
        // 200px bottom padding so the last few rows aren't flush against the
        // viewport bottom — gives the user breathing room to click / hover
        // the deepest items in the tree.
        style={{ maxHeight: 'calc(100vh - 250px)', paddingBottom: 200 }}
      >
        <ConfigProvider theme={SIDEBAR_TREE_THEME}>
          {items.map((site, siteIdx) => {
            // Per-site Menu. Site AND every Building inside are
            // non-clickable section headers in Studio (chevron hidden
            // via `index.css`), so their keys are always force-included
            // in `openKeys`. Floors inside still toggle individually to
            // show / hide rooms.
            const siteKeys = collectAllKeys(site);
            const buildingKeys = (site.children ?? []).map((b) => String(b.key));
            const siteOpen = Array.from(
              new Set([
                String(site.key),
                ...buildingKeys,
                ...openKeysWithSearch.filter((k) => siteKeys.has(k)),
              ]),
            );
            const markedSite = markedItems[siteIdx] ?? site;
            return (
              <React.Fragment key={String(site.key)}>
                <Menu
                  data-id="sidebar-menu"
                  mode="inline"
                  inlineIndent={16}
                  items={[markedSite]}
                  selectedKeys={selectedKeys}
                  style={MENU_STYLE}
                  openKeys={siteOpen}
                  onOpenChange={(ks) => {
                    // While searching, record keys the user explicitly
                    // collapsed so the search auto-expand union doesn't
                    // immediately re-open them.
                    if (filterValue) {
                      const next = new Set(ks);
                      siteOpen.forEach((k) => {
                        if (!next.has(k)) recordUserClose(String(k));
                      });
                    }
                    setOpenKeys((prev) => [...prev.filter((k) => !siteKeys.has(k)), ...ks]);
                  }}
                  className="sidebar-studio-menu"
                />
                {siteIdx < items.length - 1 && <div style={{ height: 8 }} />}
              </React.Fragment>
            );
          })}
        </ConfigProvider>
      </div>
    </>
  );
};

// ============================================================================
// Item builders — Studio variant. Site and Building have NO click navigation
// (they only toggle expand). Floor / Room / Zone navigate normally.
// ============================================================================

function buildSiteItem(
  site: FilteredSite,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: site.id,
    label: (
      <EllipsisRowTooltip name={site.name}>
        <div className="group flex w-full items-center overflow-hidden">
          <div className="flex items-center justify-between min-w-0 flex-1 mr-1">
            {/* Site rows in Studio are non-clickable section headers
             * (clicking only toggles expand). Render text + icon in
             * the tertiary content color so users don't read this row
             * as a navigation target. */}
            <span className="flex items-center min-w-0 overflow-hidden text-content-tertiary">
              <Icon name="icon-siteoutlined" className="mr-2 shrink-0" />
              <MiddleTruncatedText
                text={site.name}
                match={ctx.filterValue}
                className="flex-1 min-w-0"
              />
            </span>
            <span
              className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <SiteEditMenu id={site.id} blockDelete={(site.buildings ?? []).length > 0} />
            </span>
          </div>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(site.buildings.map((b) => buildBuildingItem(b, ctx))),
  };
}

function buildBuildingItem(
  building: FilteredBuilding,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: building.id,
    label: (
      <EllipsisRowTooltip name={building.name}>
        <div className="group flex w-full items-center overflow-hidden text-ellipsis">
          {/* No onClick — Building only toggles antd SubMenu expand/collapse. */}
          <div className="flex items-center justify-between min-w-0 flex-1 mr-1">
            {/* Building rows in Studio are non-clickable section headers
             * (Studio's canvas keys off `?spaceId=<floor>` only). Render
             * text + icon in the tertiary content color, matching the
             * Site row styling. */}
            <span className="flex items-center min-w-0 overflow-hidden text-content-tertiary">
              <Icon name="building" className="mr-2 shrink-0" />
              <MiddleTruncatedText
                text={building.name}
                match={ctx.filterValue}
                className="flex-1 min-w-0"
              />
            </span>
            <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <BuildingEditMenu id={building.id} blockDelete={building.floors.length > 0} />
            </span>
          </div>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(building.floors.map((f) => buildFloorItem(f, ctx))),
  };
}

function buildFloorItem(
  floor: FilteredFloor,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: floor.id,
    label: (
      <EllipsisRowTooltip name={floor.name}>
        <div
          className="flex items-center overflow-hidden text-ellipsis hover:rounded-r-full"
          onClick={() => updateUrlParams(ctx.navigate, { floorId: floor.id })}
        >
          <span className="flex items-center min-w-0 overflow-hidden">
            <Icon name="layer" className="mr-2" />
            <MiddleTruncatedText
              text={floor.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </span>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(floor.roomszones.map((item) => buildRoomZoneItem(floor, item, ctx))),
  };
}

function buildRoomZoneItem(
  floor: FilteredFloor,
  item: RoomZoneItem,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  const onClick = () =>
    item.kind === 'room'
      ? updateUrlParams(ctx.navigate, { floorId: floor.id, roomId: item.id })
      : updateUrlParams(ctx.navigate, { floorId: floor.id, zoneId: item.id });
  const result: MenuItem = {
    key: item.id,
    label: (
      <EllipsisRowTooltip name={item.name}>
        <div
          className="flex items-center overflow-hidden whitespace-nowrap text-ellipsis"
          onClick={onClick}
        >
          <span className="flex items-center min-w-0 overflow-hidden">
            <Icon name={item.kind === 'room' ? 'room' : 'zone'} className="mr-2" />
            <MiddleTruncatedText
              text={item.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </span>
        </div>
      </EllipsisRowTooltip>
    ),
  };
  if (item.kind === 'room' && item.zones.length > 0) {
    result.children = item.zones.map((zone) => ({
      key: zone.id,
      label: (
        <EllipsisRowTooltip name={zone.name}>
          <div
            className="flex items-center min-w-0 overflow-hidden"
            onClick={() => updateUrlParams(ctx.navigate, { floorId: floor.id, zoneId: zone.id })}
          >
            <Icon name="zone" className="mr-2" />
            <MiddleTruncatedText
              text={zone.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </div>
        </EllipsisRowTooltip>
      ),
    }));
  }
  return result;
}
